import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, User as UserIcon, Loader2 } from 'lucide-react';
import { useCompanyContactCandidates, type ContactCandidate } from '@/hooks/useCompanyContactCandidates';

interface ShareContactSelectorProps {
  companyId: string;
  /** Current user id to default to when value is unset */
  currentUserId: string | null | undefined;
  /** Selected contact user id (controlled). null/undefined = default to current user */
  value: string | null | undefined;
  onChange: (userId: string) => void;
  disabled?: boolean;
  helperText?: string;
}

export function ShareContactSelector({
  companyId,
  currentUserId,
  value,
  onChange,
  disabled,
  helperText,
}: ShareContactSelectorProps) {
  const { candidates, loading } = useCompanyContactCandidates(companyId);
  const selectedId = value || currentUserId || candidates[0]?.user_id || '';
  const selected: ContactCandidate | undefined = candidates.find((c) => c.user_id === selectedId);
  const missing: string[] = [];
  if (selected && !selected.email) missing.push('email');
  if (selected && !selected.phone) missing.push('phone');

  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
        <UserIcon className="w-3.5 h-3.5" />
        Contact shown to recipients
      </Label>
      {loading && candidates.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading team…
        </div>
      ) : (
        <Select value={selectedId} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger>
            <SelectValue placeholder="Choose a teammate" />
          </SelectTrigger>
          <SelectContent>
            {candidates.map((c) => (
              <SelectItem key={c.user_id} value={c.user_id}>
                {c.full_name}
                {c.is_self ? ' (you)' : ''}
                {c.role === 'admin' ? ' · Admin' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {selected && (
        <div className="rounded-md border border-border bg-muted/30 p-2.5 text-xs space-y-0.5">
          <p className="font-medium text-foreground">{selected.full_name}</p>
          <p className="text-muted-foreground">{selected.email || <span className="italic">no email on profile</span>}</p>
          <p className="text-muted-foreground">{selected.phone || <span className="italic">no phone on profile</span>}</p>
        </div>
      )}
      {missing.length > 0 && (
        <p className="text-xs text-warning flex items-center gap-1.5">
          <AlertTriangle className="w-3 h-3" />
          This person has no {missing.join(' or ')} on their profile — recipients won't see it.
        </p>
      )}
      {helperText && <p className="text-xs text-muted-foreground">{helperText}</p>}
    </div>
  );
}